import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import HomePage from './pages/HomePage';
import ContextPage from './pages/ContextPage';
import AgentsPage from './pages/AgentsPage';
import QAPage from './pages/QAPage';
import ReportsPage from './pages/ReportsPage';
import BusinessPlanQAPage from './pages/BusinessPlanQAPage';
import BusinessPlanPage from './pages/BusinessPlanPage';
import FinancialForecastQAPage from './pages/FinancialForecastQAPage';
import FinancialForecastPage from './pages/FinancialForecastPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import SessionsPage from './pages/SessionsPage';
import './App.css';

/**
 * ProtectedRoute — redirects unauthenticated users to /login.
 * Shows a loading indicator while AuthContext is initialising.
 */
function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <p style={{ color: '#888', margin: 0 }}>Loading...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />

      {/* Protected routes */}
      <Route
        path="/sessions"
        element={
          <ProtectedRoute>
            <SessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/context/:sessionId"
        element={
          <ProtectedRoute>
            <ContextPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:sessionId"
        element={
          <ProtectedRoute>
            <AgentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qa/:sessionId"
        element={
          <ProtectedRoute>
            <QAPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/:sessionId"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-plan-qa/:sessionId"
        element={
          <ProtectedRoute>
            <BusinessPlanQAPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-plan/:sessionId"
        element={
          <ProtectedRoute>
            <BusinessPlanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial-forecast-qa/:sessionId"
        element={
          <ProtectedRoute>
            <FinancialForecastQAPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial-forecast/:sessionId"
        element={
          <ProtectedRoute>
            <FinancialForecastPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
